import React, { useState } from 'react'
import { useAppSelector } from '../../../../../../../../../../app/hooks'
import {
  AttachmentData,
  AttachmentDataWithRequiredAttachment,
  AttachmentStatus,
  AttachmentType,
} from '../../../../../../../../../../@types/types'
import { PictureCarousel } from './PictureCarousel'
import { useMultimediaDetailsModalContext } from '../../../../multimediaDetailsModalContext/multimediaModalsContext'
import { createAndTriggerPluploadFileInput } from '../../../utils/fileUploadUtils'
import styles from './MobileMultimediaPreview.module.css'
import { AddPictureButton } from '../../../../multimediaDetailsModal/modal/webView/components-mobile/addPictureButton/AddPictureButton'
import { DraggablePictureList } from '../../../../multimediaDetailsModal/modal/webView/components-mobile/draggablePictureList/DraggablePictureList'
import { usePersistedPictureReorder } from '../../../../multimediaDetailsModal/modal/webView/components-mobile/usePersistedPictureReorder'

interface MobileMultimediaPreviewProps {
  dropzoneId: string
  pictureAttachments?: AttachmentData[]
  pictures: AttachmentDataWithRequiredAttachment[]
  isInlinePicturesEnabled?: boolean
  onPictureDeleted?: (pictureId: string) => void
}

export const WebViewMultimediaPreview: React.FC<MobileMultimediaPreviewProps> = ({
  dropzoneId,
  pictures,
  pictureAttachments = pictures,
  isInlinePicturesEnabled = false,
  onPictureDeleted,
}) => {
  const attachments = useAppSelector((state) => state.multimediaFeature.attachments) ?? []
  const { openModalForEditClick, openDetailsModalForSelectedAttachments, onAttachmentDelete } =
    useMultimediaDetailsModalContext()
  const [selectedPictureId, setSelectedPictureId] = useState<string>()
  const handleReorder = usePersistedPictureReorder(pictureAttachments)
  const selectedPicture = pictures.find((item) => item.attachment.id === selectedPictureId) ?? pictures[0]

  const handleEditClick = (picture: AttachmentDataWithRequiredAttachment) => {
    if (isInlinePicturesEnabled) {
      openDetailsModalForSelectedAttachments(pictures, true, pictures.indexOf(picture))
      return
    }

    const modalAttachments = attachments.filter(
      (item) => item.attachmentType === AttachmentType.PICTURE || item.attachmentType === AttachmentType.DOCUMENT
    )
    openModalForEditClick(modalAttachments.indexOf(picture))
  }

  const handleDeleteClick = async (picture: AttachmentDataWithRequiredAttachment) => {
    const deletedIndex = pictures.findIndex((item) => item.attachment.id === picture.attachment.id)
    const nextSelectedPicture = pictures[deletedIndex + 1] ?? pictures[deletedIndex - 1]
    await onAttachmentDelete({
      ...picture.attachment,
      size: picture.file?.size ?? picture.attachment.size,
    })
    onPictureDeleted?.(picture.attachment.id)
    setSelectedPictureId(nextSelectedPicture?.attachment.id)
  }

  return (
    <>
      {selectedPicture && (
        <div className={`relative w-full ${styles.previewContainer}`}>
          <PictureCarousel
            attachments={pictures}
            selectedPictureId={selectedPicture.attachment.id}
            onSelect={setSelectedPictureId}
            onEditClick={handleEditClick}
            onDeleteClick={isInlinePicturesEnabled ? handleDeleteClick : undefined}
            onAddClick={createAndTriggerPluploadFileInput}
            dropzoneId={dropzoneId}
          />
        </div>
      )}
      {isInlinePicturesEnabled && (
        <DraggablePictureList
          attachments={pictureAttachments}
          selectedIds={selectedPicture ? [selectedPicture.attachment.id] : []}
          onSelect={(pictureId) => {
            const picture = pictureAttachments.find(
              (item) => item.state === AttachmentStatus.PERSISTED && item.attachment?.id === pictureId
            )
            if (picture) {
              setSelectedPictureId(pictureId)
            }
          }}
          onReorder={handleReorder}
          variant='strip'
          leadingContent={<AddPictureButton onClick={createAndTriggerPluploadFileInput} />}
        />
      )}
    </>
  )
}
